import { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';
import { getSocket } from '../lib/socket';
import type { PowerSessionUpdatePayload } from '@marnic/shared/events';
import type { CampaignStatus } from '@marnic/shared';

interface Props {
  campaignId: string;
  linesPerAgent: number;
  campaignStatus: CampaignStatus;
}

type SessionStatus = PowerSessionUpdatePayload['status'] | 'none';

export function PowerSessionPanel({ campaignId, linesPerAgent, campaignStatus }: Props): JSX.Element {
  const [sessionStatus, setSessionStatus] = useState<SessionStatus>('none');
  const [batchId, setBatchId] = useState<string | null>(null);
  const [candidateCount, setCandidateCount] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const socket = getSocket();
    if (!socket) return;
    const handler = (update: PowerSessionUpdatePayload): void => {
      if (update.campaignId !== campaignId) return;
      setSessionStatus(update.status);
      setBatchId(update.batchId);
      setCandidateCount(update.candidateCount);
      if (update.reason) {
        if (update.reason === 'NO_LEADS') setError('No more leads in the queue.');
        else if (update.reason === 'BATCH_EXHAUSTED') setError(null);
        else if (update.reason === 'ABANDONED_RATE_EXCEEDED') setError('Abandon rate limit reached — campaign paused.');
        else setError(null);
      } else {
        setError(null);
      }
    };
    socket.on('power_session_update', handler);
    return () => { socket.off('power_session_update', handler); };
  }, [campaignId]);

  const startSession = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.post<{ ok: boolean; error?: string }>(`/campaigns/${campaignId}/power/start`);
      if (!res.ok) setError(res.error ?? 'Could not start session.');
    } catch {
      setError('Could not start session.');
    } finally {
      setLoading(false);
    }
  }, [campaignId]);

  const dialNext = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.post<{ ok: boolean; error?: string; reason?: string; abandonRate?: number }>(`/campaigns/${campaignId}/power/next`);
      if (!res.ok) {
        if (res.reason === 'OUT_OF_HOURS') setError('Outside calling hours.');
        else if (res.reason === 'ABANDONED_RATE_EXCEEDED') setError(`Abandon rate limit reached (${((res.abandonRate ?? 0) * 100).toFixed(1)}%).`);
        else setError(res.error ?? 'Could not start batch.');
      }
    } catch {
      setError('Could not start batch.');
    } finally {
      setLoading(false);
    }
  }, [campaignId]);

  const stopSession = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      await api.post(`/campaigns/${campaignId}/power/stop`);
    } catch {
      setError('Could not stop session.');
    } finally {
      setLoading(false);
    }
  }, [campaignId]);

  const canStart = campaignStatus === 'active';

  return (
    <div className="card p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-ink-500">Power session</h3>
        <span className="text-xs text-ink-400">{linesPerAgent} line{linesPerAgent !== 1 ? 's' : ''} / batch</span>
      </div>

      {error && (
        <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</p>
      )}

      {sessionStatus === 'none' || sessionStatus === 'stopped' ? (
        <button
          type="button"
          className="btn-primary w-full"
          onClick={() => void startSession()}
          disabled={loading || !canStart}
        >
          {loading ? 'Starting…' : 'Start session'}
        </button>
      ) : sessionStatus === 'idle' || sessionStatus === 'wrap_up' ? (
        <div className="flex gap-2">
          <button
            type="button"
            className="btn-primary flex-1"
            onClick={() => void dialNext()}
            disabled={loading}
          >
            {loading ? 'Launching…' : `Dial next batch`}
          </button>
          <button
            type="button"
            className="btn-secondary"
            onClick={() => void stopSession()}
            disabled={loading}
          >
            Stop
          </button>
        </div>
      ) : sessionStatus === 'dialing' ? (
        <div className="flex items-center gap-3">
          <div className="flex-1">
            <p className="text-sm font-medium">Dialling {candidateCount} number{candidateCount !== 1 ? 's' : ''}…</p>
            <p className="text-xs text-ink-400">Waiting for first answer</p>
          </div>
          <button
            type="button"
            className="btn-secondary text-sm"
            onClick={() => void stopSession()}
            disabled={loading}
          >
            Stop
          </button>
        </div>
      ) : sessionStatus === 'connected' ? (
        <div className="space-y-1">
          <p className="text-sm font-medium text-green-700">Connected — disposition to unlock next batch</p>
          {batchId && <p className="text-xs text-ink-400 font-mono">Batch {batchId.slice(0, 8)}</p>}
        </div>
      ) : null}
    </div>
  );
}
